
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { touchActivity, hasActiveSession, getDashboardPath } from '../utils/sessionUtils';
import TenantSelectionModal from './shared/TenantSelectionModal';
import PasswordInput from './shared/PasswordInput';

function Login() {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Multi-tenant selection state
  const [tenantSelection, setTenantSelection] = useState(null); // { tenants, loginType }

  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

  // Already signed in (e.g. pressed Back from inside the app): never show
  // the login form — send them to their dashboard instead. replace so the
  // login entry doesn't stay in history and trap Back in a loop.
  useEffect(() => {
    if (hasActiveSession()) {
      navigate(getDashboardPath(), { replace: true });
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value });
  };

  /** Stores session data in localStorage and navigates to dashboard.
   *
   * Phase 2 of the JWT cookie migration: we no longer write the JWT to
   * localStorage. The server now issues the token as an httpOnly cookie
   * (see authRoutes.js + utils/authCookie.js) which can't be exfiltrated
   * by an XSS payload. The rest of the session metadata stays in
   * localStorage because it's used purely for UI state (current tenant
   * id, username, role) and doesn't authenticate any request.
   */
  const completeLogin = (data) => {
    const { user, sessionId, isReused } = data;
    const { tenantid, id: ownerid, username } = user;

    // PHASE 2 — DO NOT WRITE data.token TO LOCALSTORAGE.
    // Cookie does the work now. Any code still reading
    // localStorage.authToken will simply find an empty string and the
    // server's sessionFromHeaders middleware will use the cookie instead.
    localStorage.setItem('sessionId', sessionId);
    // Only persist a real tenant id. If the owner has no tenant yet,
    // remove any stale key so consumers don't read the literal string
    // "null" and try to navigate to /null/dashboard or query
    // /api/.../null/...
    if (tenantid) {
      localStorage.setItem('tenantid', String(tenantid));
      localStorage.setItem('tenantId', String(tenantid));
    } else {
      localStorage.removeItem('tenantid');
      localStorage.removeItem('tenantId');
    }
    localStorage.setItem('ownerid', ownerid);
    localStorage.setItem('userId', String(ownerid));
    localStorage.setItem('username', username);
    localStorage.setItem('role', user.role || 'operator');

    try {
      localStorage.setItem('me', JSON.stringify({ id: user.id, tenantId: user.tenantid || null }));
      window.__ME__ = { id: user.id, tenantId: user.tenantid || null };
    } catch {}

    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }

    // Track terms acceptance status
    if (user.termsAcceptedAt) {
      localStorage.setItem('termsAcceptedAt', user.termsAcceptedAt);
    } else {
      localStorage.removeItem('termsAcceptedAt');
    }

    if (isReused) {
      console.log('Session reused - user was previously logged in');
    } else {
      console.log('New session created');
    }

    touchActivity();

    const returnTo = searchParams.get('returnTo');
    if (returnTo && returnTo.startsWith('/')) {
      navigate(returnTo, { replace: true });
      return;
    }

    // A freshly-registered owner has no tenant yet — sending them to
    // /:tenant/dashboard with tenantid=null produces /null/dashboard, and
    // every authenticated API call on that page 401s because the JWT
    // carries no tenant. Route them to /tenantadd instead so they can
    // create their first tenant; the /registertenant response will
    // refresh the cookie with the new tenant id.
    if (!tenantid) {
      navigate('/tenantadd', { state: user, replace: true });
      return;
    }
    // replace: /login must not sit in history behind the dashboard.
    navigate(`/${tenantid}/dashboard`, { replace: true });
  };

  const login = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Endpoint is /api/auth/login on the server (routes/authRoutes.js).
      // /api/users/* is no longer mounted with a login handler.
      const response = await axios.post(`${baseUrl}/api/auth/login`, credentials, {
        withCredentials: true
      });

      if (response.status === 200 && response.data.success) {
        // Check if backend is asking for tenant selection
        if (response.data.requiresTenantSelection) {
          setTenantSelection({
            tenants: response.data.tenants,
            loginType: response.data.loginType
          });
          setIsLoading(false);
          return;
        }

        // Single tenant - complete login directly
        completeLogin(response.data);
      } else {
        toast.error(response.data.message || t('common.unexpectedError'));
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error(t('auth.invalidCredentialsError'));
      } else if (error.response?.status === 403) {
        const errorData = error.response.data;
        toast.error(
          errorData.message || t('auth.maxConcurrentUsersError'),
          { autoClose: 8000 }
        );
        if (errorData.details) {
          console.log('User limit details:', errorData.details);
        }
      } else {
        console.error('Login error:', error);
        toast.error(t('auth.loginFailedError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  /** Called when user picks a tenant from the selection modal */
  const handleTenantSelect = async (tenantId) => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}/api/users/select-tenant`,
        {
          username: credentials.username,
          password: credentials.password,
          tenantId,
          loginType: tenantSelection.loginType
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        setTenantSelection(null);
        completeLogin(response.data);
      } else {
        toast.error(response.data.message || t('common.unexpectedError'));
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(
          error.response.data?.message || t('auth.maxConcurrentUsersError'),
          { autoClose: 8000 }
        );
      } else {
        console.error('Tenant selection error:', error);
        toast.error(t('auth.loginFailedError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOwner = () => navigate(`/ownerAdd`);

  return (
    // `keep-default-theme` opts the login screen out of the Minimal theme
    // overrides in styles/overrides.css so the form, inputs and buttons
    // always render with the default light-card look — independent of
    // whatever theme the previous logged-in user had chosen.
    <div className="keep-default-theme" style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '20px',
      boxSizing: 'border-box'
    }}>
      {/* Logo — sized so the visible "On Screen" mark renders ~5% larger
          than the removed "Login Here" heading did. The heading rendered
          at roughly 150px wide ("Login Here" in 1.75rem bold); 5% larger
          ≈ 158px of visible content. The logo PNG only fills ~90% of its
          bounding box horizontally (transparent padding on both sides),
          so the <img> element itself is set to 11rem (~176px) to land
          the visible mark at ~158px. Sits in the flex:1 spacer above the
          card so it is vertically centered between the top of the
          viewport and the top of the login card (just above the Username
          label); the matching flex:1 spacer below keeps the card in its
          previous centered position. */}
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%'
      }}>
        <img
          src={`${process.env.PUBLIC_URL}/on-screen-logo-white-bezel-h160.png`}
          alt="On Screen"
          style={{ width: '11rem', height: 'auto' }}
        />
      </div>
      <div style={{ width: '100%', maxWidth: '450px' }}>
        <div className="card">
          <div className="card-body">
            <form onSubmit={login}>
              <div className="form-group">
                <label htmlFor="username">{t('auth.usernameLabel')}</label>
                <input
                  className="textInput"
                  type="text"
                  name="username"
                  value={credentials.username}
                  onChange={handleInputChange}
                  placeholder={t('auth.usernamePlaceholder')}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">{t('auth.passwordLabel')}</label>
                <PasswordInput
                  className="textInput"
                  name="password"
                  autoComplete="current-password"
                  value={credentials.password}
                  onChange={handleInputChange}
                  placeholder={t('auth.passwordPlaceholder')}
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Forgot password link */}
              <div className="mt-2">
                <Link to="/reset-password" className="text-sm text-blue-600 hover:underline">
                  {t('auth.forgotPasswordLink')}
                </Link>
              </div>

              <button
                className="btn btn-primary"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? t('auth.loggingInButton') : t('auth.loginButton')}
              </button>
              <button
                onClick={handleOwner}
                className="btn btn-primary"
                type="button"
                disabled={isLoading}
              >
                {t('auth.createAccountButton')}
              </button>
            </form>
          </div>
        </div>

        {/* Footer with Terms of Service and Privacy Policy links */}
        <footer style={{
          borderTop: '1px solid #e0e0e0',
          padding: '20px',
          textAlign: 'center',
          marginTop: '40px'
        }}>
          <div>
            <Link
              to="/termsofuse"
              style={{ margin: '0 15px', color: '#0066cc', textDecoration: 'none' }}
            >
              {t('common.termsOfServiceLink')}
            </Link>
            <span style={{ color: '#999' }}>|</span>
            <Link
              to="/privacypolicy"
              style={{ margin: '0 15px', color: '#0066cc', textDecoration: 'none' }}
            >
              {t('common.privacyPolicyLink')}
            </Link>
          </div>
          <p style={{ fontSize: '12px', color: '#666', marginTop: '10px', marginBottom: '0' }}>
            {t('common.copyrightText', { year: new Date().getFullYear() })}
          </p>
        </footer>
      </div>
      {/* Matching spacer below the card so the card+footer wrapper stays
          vertically centered (same visual position as before the logo
          was added above it). */}
      <div style={{ flex: 1, width: '100%' }} />

      {/* Tenant selection modal - shown when user has multiple tenants */}
      {tenantSelection && (
        <TenantSelectionModal
          tenants={tenantSelection.tenants}
          onSelect={handleTenantSelect}
          onCancel={() => setTenantSelection(null)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

export default Login;